import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const MinimalPairsMemoryGame = ({ gameData, onComplete }) => {
    const [cards, setCards] = useState([]);
    const [flippedCards, setFlippedCards] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [score, setScore] = useState(0);
    const [moves, setMoves] = useState(0);
    const [showExplanation, setShowExplanation] = useState(null);
    const [feedback, setFeedback] = useState(null); 

    useEffect(() => {
        if (gameData && gameData.pairs) {
            const allCards = [];
            gameData.pairs.forEach(pair => {
                allCards.push({
                    id: `${pair.id}-word1`,
                    pairId: pair.id,
                    word: pair.word1.text,
                    emoji: pair.word1.emoji,
                    soundFocus: pair.word1.sound_focus,
                    type: 'word1'
                });
                allCards.push({
                    id: `${pair.id}-word2`,
                    pairId: pair.id,
                    word: pair.word2.text,
                    emoji: pair.word2.emoji,
                    soundFocus: pair.word2.sound_focus,
                    type: 'word2'
                });
            });

            const shuffled = allCards.sort(() => Math.random() - 0.5);
            setCards(shuffled);
        }
    }, [gameData]);

    const handleCardClick = (card) => {
        if (flippedCards.find(c => c.id === card.id) || matchedPairs.includes(card.pairId) || feedback) {
            return;
        }

        triggerHaptic(20);
        const newFlipped = [...flippedCards, card];
        setFlippedCards(newFlipped);

        if (newFlipped.length === 2) {
            setMoves(moves + 1);

            if (newFlipped[0].pairId === newFlipped[1].pairId) {
                const pair = gameData.pairs.find(p => p.id === card.pairId);
                setMatchedPairs(prev => [...prev, card.pairId]);
                const newScore = score + (gameData.points_per_pair || 25);
                setScore(newScore);
                setShowExplanation(pair);
                setFeedback('correct');
                triggerHaptic([100, 50, 100]);

                setTimeout(() => {
                    setFlippedCards([]);
                    setShowExplanation(null);
                    setFeedback(null);

                    if (matchedPairs.length + 1 === gameData.pairs.length) {
                        triggerHaptic([200, 100, 200, 100, 300]);
                        onComplete?.(newScore);
                    }
                }, 4000);
            } else {
                triggerHaptic([50, 50, 50]);
                setTimeout(() => {
                    setFlippedCards([]);
                }, 1200);
            }
        }
    };

    if (!gameData || !gameData.pairs) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🧠</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Ruošiamos kortelės...</h3>
            </div>
        );
    }

    const isCardFlipped = (card) => flippedCards.find(c => c.id === card.id) || matchedPairs.includes(card.pairId);
    const isCardMatched = (card) => matchedPairs.includes(card.pairId);
    const progress = (matchedPairs.length / gameData.pairs.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🧠
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Atminties Detektyvas
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Ėjimai</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{moves}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-purple-500 to-indigo-700 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(99,102,241,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            <div className="premium-glass rounded-[2rem] md:rounded-[3rem] p-4 md:p-8 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden text-center">
                <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.4em] mb-2 md:mb-4">Instrukcija</h4>
                <p className="text-xs md:text-lg font-bold text-slate-600 leading-tight">
                    Surask poras, kurios skiriasi tik vienu garsu! 🔎
                </p>
            </div>
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-6 flex flex-col items-center w-full max-w-5xl mx-auto">
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-3 md:p-8 shadow-2xl border-white/60 bg-slate-900 relative overflow-hidden w-full">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1)_0%,transparent_80%)] pointer-events-none" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-6 relative z-10 p-2 md:p-6">
                    {cards.map((card) => {
                        const flipped = isCardFlipped(card);
                        const matched = isCardMatched(card);

                        return (
                            <button
                                key={card.id}
                                onClick={() => handleCardClick(card)}
                                disabled={flipped || matched || !!feedback}
                                className={`
                                    relative aspect-[3/4] rounded-xl md:rounded-[3rem] transition-all duration-700 transform-gpu preserve-3d
                                    ${flipped || matched ? '[transform:rotateY(180deg)]' : 'hover:scale-105 active:scale-95 shadow-2xl'}
                                    disabled:cursor-default
                                `}
                            >
                                {/* Front Side (Hidden) */}
                                <div className="absolute inset-0 backface-hidden flex flex-col items-center justify-center bg-slate-800 rounded-xl md:rounded-[3rem] border-4 border-slate-700 shadow-[inset_0_4px_20px_rgba(0,0,0,0.5)]">
                                    <div className="text-2xl md:text-6xl text-slate-600 font-black opacity-20 drop-shadow-lg">?</div>
                                </div>

                                {/* Back Side (Revealed) */}
                                <div className={`
                                    absolute inset-0 backface-hidden [transform:rotateY(180deg)] flex flex-col items-center justify-center p-2 md:p-6 rounded-xl md:rounded-[3rem] border-4
                                    ${matched 
                                        ? 'bg-emerald-500 border-emerald-400 text-white shadow-[0_0_40px_rgba(16,185,129,0.4)]' 
                                        : 'bg-indigo-600 border-indigo-400 text-white shadow-[0_0_40px_rgba(99,102,241,0.4)]'}
                                `}>
                                    <div className="text-4xl md:text-7xl mb-2 md:mb-4 drop-shadow-2xl animate-float">{card.emoji}</div>
                                    <div className="text-sm md:text-2xl font-black text-center leading-none tracking-tighter uppercase">
                                        {card.word}
                                    </div>
                                    <div className={`mt-2 md:mt-4 text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] px-2 md:px-4 py-1 md:py-2 rounded-full border-2 ${matched ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20'}`}>
                                        {card.soundFocus}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback === 'correct' && showExplanation && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className="max-w-3xl w-full p-8 md:p-16 rounded-[3rem] md:rounded-[5rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px] border-emerald-500">
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            🔎
                        </div>
                        
                        <div className="text-center space-y-6 pt-4">
                            <h2 className="text-2xl md:text-5xl font-black uppercase tracking-tighter text-emerald-600 leading-none">Pora Surasta!</h2>
                            
                            <div className="flex items-center justify-center gap-2 md:gap-4">
                                <div className="flex-1 bg-emerald-50 rounded-2xl md:rounded-[3rem] p-4 md:p-8 text-center border-4 border-white shadow-xl animate-slide-right">
                                    <div className="text-4xl md:text-7xl mb-2 md:mb-4 drop-shadow-xl">{showExplanation.word1.emoji}</div>
                                    <div className="text-sm md:text-2xl font-black text-slate-800 uppercase tracking-tighter">{showExplanation.word1.text}</div>
                                    <div className="text-[8px] md:text-xs font-black text-emerald-500 uppercase tracking-widest mt-2">{showExplanation.word1.sound_focus}</div>
                                </div>
                                <div className="text-xl md:text-4xl text-emerald-200 font-black animate-pulse">↔️</div>
                                <div className="flex-1 bg-emerald-50 rounded-2xl md:rounded-[3rem] p-4 md:p-8 text-center border-4 border-white shadow-xl animate-slide-left">
                                    <div className="text-4xl md:text-7xl mb-2 md:mb-4 drop-shadow-xl">{showExplanation.word2.emoji}</div>
                                    <div className="text-sm md:text-2xl font-black text-slate-800 uppercase tracking-tighter">{showExplanation.word2.text}</div>
                                    <div className="text-[8px] md:text-xs font-black text-emerald-500 uppercase tracking-widest mt-2">{showExplanation.word2.sound_focus}</div>
                                </div>
                            </div>

                            <div className="bg-indigo-900 rounded-3xl md:rounded-[4rem] p-6 md:p-10 text-white shadow-inner relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.1] select-none pointer-events-none">📜</div>
                                <div className="relative z-10 space-y-2 md:space-y-4">
                                    <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em]">Linguistinis Skirtumas</h4>
                                    <p className="text-base md:text-3xl font-black tracking-tighter leading-none italic font-serif text-indigo-100">
                                        {showExplanation.sound_difference}
                                    </p>
                                    <div className="w-16 h-1 bg-emerald-500 mx-auto rounded-full" />
                                    <p className="text-xs md:text-xl text-indigo-200/80 font-bold italic font-serif leading-relaxed px-4">
                                        "{showExplanation.explanation}"
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default MinimalPairsMemoryGame;
